import { PackageSearch } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SearchInput } from '~/components/common/search-input';
import { Label } from '~/components/ui/label';
import { RadioGroup, RadioGroupItem } from '~/components/ui/radio-group';
import { Skeleton } from '~/components/ui/skeleton';
import { StatusChip } from '~/features/catalog-operations/components/chips';
import {
  EXCLUDED_SCHEDULE_JOB_SOURCES,
  JOB_SOURCE_LABELS,
  SCHEDULE_JOB_PICKER_SIZE,
} from '~/features/catalog-operations/constants/schedules';
import { useBatches } from '~/features/catalog-operations/hooks/use-catalog-batches';
import { cn } from '~/utils/cn';
import { formatDateShort } from '~/utils/date';

interface Props {
  selectedJobId: string | null;
  onSelect: (jobId: string) => void;
  /** Rendered above the list — e.g. a 404 from a batch that has since been
   *  deleted, or a 422 from one the backend won't schedule. */
  banner?: React.ReactNode;
  /** E2E `data-testid` root; children derive `${testId}-...`. */
  testId?: string;
}

/** A small, quiet fact pill used for a batch's platform / use-case / origin. */
function MetaChip({ children }: { children: React.ReactNode }) {
  return (
    <span className="bg-muted text-muted-foreground inline-flex items-center rounded-md px-1.5 py-0.5 text-xs font-medium">
      {children}
    </span>
  );
}

/**
 * Step 1 of the wizard: pick the batch to put on a cadence.
 *
 * Schedules are job-first — the create call takes a `jobId` and snapshots that
 * job's config, so there is nothing to configure here.
 *
 * Eligibility is still the server's call (`POST /preview` → 422 on Continue), and
 * each row shows its origin so that decision is legible. The one thing filtered
 * client-side is {@link EXCLUDED_SCHEDULE_JOB_SOURCES}: trial and feed-audit
 * batches are hidden because neither is something you put on a repeating cadence.
 */
export function ScheduleJobPicker({
  selectedJobId,
  onSelect,
  banner,
  testId = 'schedules-job-picker',
}: Props) {
  const { t } = useTranslation('catalog-operations');
  const [search, setSearch] = useState('');

  // Unfiltered, and the same params as the listing page's own probe fetch — so
  // this shares that query's cache entry instead of firing a second request for
  // rows already on screen.
  const { data, isLoading } = useBatches({
    tab: 'all',
    search: '',
    page: 0,
    size: SCHEDULE_JOB_PICKER_SIZE,
  });

  const hasSearch = search.trim() !== '';
  // Drop trial/feed-audit origins first, then apply the free-text filter. Both
  // steps are client-side over the shared listing cache.
  const jobs = useMemo(() => {
    const query = search.trim().toLowerCase();
    return (data?.items ?? []).filter((job) => {
      if (EXCLUDED_SCHEDULE_JOB_SOURCES.has(job.source)) return false;
      return (
        !query ||
        job.name.toLowerCase().includes(query) ||
        job.batchId.toLowerCase().includes(query)
      );
    });
  }, [data?.items, search]);

  return (
    <div data-testid={testId} className="space-y-4">
      {banner}

      <div>
        <Label htmlFor="schedule-job-search" className="mb-1.5 block text-sm font-medium">
          {t('schedules.wizard.findBatch')}
        </Label>
        <SearchInput
          id="schedule-job-search"
          testId={`${testId}-search`}
          value={search}
          onChange={setSearch}
          placeholder={t('schedules.wizard.findBatchPlaceholder')}
        />
        <p className="text-muted-foreground mt-1.5 text-xs">
          {t('schedules.wizard.findBatchHint')}
        </p>
      </div>

      {isLoading ? (
        <div data-testid={`${testId}-loading`} className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-[74px] w-full rounded-xl" />
          ))}
        </div>
      ) : jobs.length === 0 ? (
        <div
          data-testid={`${testId}-empty`}
          className="flex flex-col items-center gap-2 rounded-xl border border-dashed px-5 py-10 text-center"
        >
          <span className="bg-muted text-muted-foreground flex size-10 items-center justify-center rounded-full">
            <PackageSearch className="size-5" aria-hidden="true" />
          </span>
          {/* Two different situations: the search matched nothing, or there are
              no batches at all. Saying "no match for your search" when there is
              nothing to search sends the user hunting for a batch that never
              existed. */}
          <p className="text-muted-foreground max-w-sm text-sm">
            {hasSearch ? t('schedules.wizard.noBatches') : t('schedules.wizard.noBatchesYet')}
          </p>
        </div>
      ) : (
        <RadioGroup
          data-testid={`${testId}-list`}
          value={selectedJobId ?? ''}
          onValueChange={onSelect}
          className="max-h-[22rem] space-y-2 overflow-y-auto px-1"
        >
          {jobs.map((job) => {
            const selected = selectedJobId === job.id;
            return (
              <Label
                key={job.id}
                htmlFor={`schedule-job-${job.id}`}
                data-testid={`${testId}-item-${job.id}`}
                className={cn(
                  'flex cursor-pointer items-center gap-3 rounded-xl border p-3.5 transition-colors',
                  selected
                    ? 'border-primary bg-accent-100 ring-primary/15 ring-1'
                    : 'hover:border-primary/40 hover:bg-accent-100/50',
                )}
              >
                <RadioGroupItem
                  id={`schedule-job-${job.id}`}
                  data-testid={`${testId}-radio-${job.id}`}
                  value={job.id}
                  className="shrink-0 cursor-pointer"
                />
                <span className="min-w-0 flex-1">
                  <span className="flex min-w-0 items-center gap-2">
                    <span
                      data-testid={`${testId}-item-name-${job.id}`}
                      className="text-foreground truncate text-sm font-semibold"
                    >
                      {job.name || job.batchId}
                    </span>
                    {/* Same status pill as the batch table, shrunk to card scale
                        so the row carries the batch's live state too. */}
                    <StatusChip
                      testId={`${testId}-item-status-badge-${job.id}`}
                      status={job.status}
                      className="h-auto shrink-0 rounded-md px-2 py-0.5 text-xs"
                    />
                  </span>
                  <span className="mt-1.5 flex flex-wrap items-center gap-1.5">
                    <MetaChip>{job.sourcePlatform}</MetaChip>
                    <MetaChip>{job.enrichmentType}</MetaChip>
                    {/* The origin is what the backend judges, so it is shown
                        rather than left implicit. */}
                    <MetaChip>{JOB_SOURCE_LABELS[job.source] ?? job.source}</MetaChip>
                  </span>
                  <span
                    className="text-muted-foreground/80 mt-1.5 block truncate font-mono text-[11px]"
                    title={job.batchId}
                  >
                    {job.batchId}
                  </span>
                </span>
                <span className="flex shrink-0 flex-col items-end gap-0.5 text-right">
                  <span
                    data-testid={`${testId}-item-product-count-${job.id}`}
                    className="text-foreground text-sm font-semibold tabular-nums"
                  >
                    {t('schedules.wizard.productCount', { count: job.productCount })}
                  </span>
                  <span className="text-muted-foreground text-xs">
                    {t('schedules.wizard.lastRun', { date: formatDateShort(job.createdAt) })}
                  </span>
                </span>
              </Label>
            );
          })}
        </RadioGroup>
      )}

      <div
        data-testid={`${testId}-reimport-note`}
        className="border-info bg-info-50 text-muted-foreground rounded-lg border border-l-[3px] px-3 py-2.5 text-sm"
      >
        {/* Worth saying up front: a schedule is not a frozen snapshot of these
            products — every run re-imports the catalog. */}
        {t('schedules.wizard.reimportNote')}
      </div>
    </div>
  );
}
